import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { yieldToMainThread, getViewParameterValue, ResizeNotifier } from '@theme/utilities';

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets – The fieldset elements.
 */

/**
 * A custom element that manages a variant picker.
 *
 * Safari fix included:
 * - Do NOT trust data-variant-id (can point to first available).
 * - Always use returned server JSON variant.id as truth.
 * - Lock add-to-cart form while variant update fetch is in-flight (prevents race on Safari).
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  #resizeObserver = new ResizeNotifier(() => this.updateVariantPickerCss());

  /** @type {boolean} */
  #isUpdatingVariant = false;

  /** @type {number} */
  #updateSeq = 0;

  connectedCallback() {
    super.connectedCallback();

    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === 'true');
      if (initialCheckedIndex !== -1) {
        this.#checkedIndices.push([initialCheckedIndex]);
      } else {
        this.#checkedIndices.push([]);
      }
    });

    this.addEventListener('change', this.variantChanged.bind(this));
    this.#resizeObserver.observe(this);

    // Ensure initial form id matches the initial server-rendered JSON (single variant object)
    const initialVariantId = this.#getVariantIdFromOwnJson();
    if (initialVariantId) {
      this.updateProductFormVariantId(initialVariantId);
      this.updateUrlVariantParam(initialVariantId);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
  }

  /**
   * Disable/enable the closest add-to-cart form while variant is updating.
   * Prevents Safari race where submit happens before hidden input[name="id"] updates.
   * @param {boolean} disabled
   */
  #setFormDisabled(disabled) {
    const form =
      this.closest('form[action^="/cart/add"]') ||
      this.closest('product-form')?.querySelector('form[action^="/cart/add"]') ||
      this.closest('[data-product-section]')?.querySelector('form[action^="/cart/add"]');

    if (!form) return;

    const submits = form.querySelectorAll('button[type="submit"], input[type="submit"]');
    submits.forEach((btn) => {
      btn.disabled = disabled;
      btn.toggleAttribute('aria-busy', disabled);
    });

    const qty = form.querySelector('input[name="quantity"]');
    if (qty) qty.disabled = disabled;
  }

  /**
   * Updates the closest product form hidden variant id input.
   * Safari: dispatch both input + change to satisfy listeners.
   * @param {string} variantId
   */
  updateProductFormVariantId(variantId) {
    if (!variantId) return;

    const form =
      this.closest('form[action^="/cart/add"]') ||
      this.closest('product-form')?.querySelector('form[action^="/cart/add"]') ||
      this.closest('[data-product-section]')?.querySelector('form[action^="/cart/add"]');

    if (!form) return;

    const idInput = form.querySelector('input[name="id"], select[name="id"]');
    if (!idInput) return;

    idInput.value = String(variantId);
    idInput.dispatchEvent(new Event('input', { bubbles: true }));
    idInput.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /**
   * Update URL ?variant= param (product page only)
   * @param {string | null} variantId
   */
  updateUrlVariantParam(variantId) {
    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !this.closest('product-card') &&
      !this.closest('quick-add-dialog');

    if (!isOnProductPage) return;

    const url = new URL(window.location.href);
    if (variantId) url.searchParams.set('variant', String(variantId));
    else url.searchParams.delete('variant');

    if (url.href !== window.location.href) {
      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * Gets the variant id from this variant-picker's own JSON (single variant object).
   * @returns {string | null}
   */
  #getVariantIdFromOwnJson() {
    const jsonEl = this.querySelector('script[type="application/json"]');
    if (!jsonEl?.textContent) return null;
    try {
      const v = JSON.parse(jsonEl.textContent);
      return v?.id ? String(v.id) : null;
    } catch {
      return null;
    }
  }

  /**
   * Handles the variant change event.
   * Safari fix: do NOT trust data-variant-id. Use fetched server JSON variant.id as truth.
   * Also locks form while updating to prevent race.
   * @param {Event} event
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);
    this.dispatchEvent(new VariantSelectedEvent({ id: selectedOption.dataset.optionValueId ?? '' }));

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;
    const isOnFeaturedProductSection = Boolean(this.closest('featured-product-information'));

    const morphElementSelector = loadsNewProduct
      ? 'main'
      : isOnFeaturedProductSection
      ? 'featured-product-information'
      : undefined;

    // Safari fix: prevent add-to-cart until the correct variant id has been written
    this.#isUpdatingVariant = true;
    this.#setFormDisabled(true);
    const seq = ++this.#updateSeq;

    // Fetch updated section; response JSON contains the true variant.id
    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), morphElementSelector, seq);

    // Combined listing: update path immediately (variant id will be set after fetch)
    if (loadsNewProduct) {
      const url = new URL(window.location.href);
      url.pathname = newUrl;

      if (url.href !== window.location.href) {
        yieldToMainThread().then(() => {
          history.replaceState({}, '', url.toString());
        });
      }
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption
   * @param {string | null} [source]
   * @param {string[]} [sourceSelectedOptionsValues]
   * @returns {string}
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;

    const params = [];
    const viewParamValue = getViewParameterValue();
    if (viewParamValue) params.push(`view=${viewParamValue}`);

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else if (selectedOption.dataset.optionValueId) {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of some specific sections, we need to append section_id=xxxx to the URL
    const SECTION_ID_MAP = {
      'quick-add-component': 'section-rendering-product-card',
      'swatches-variant-picker-component': 'section-rendering-product-card',
      'featured-product-information': this.closest('featured-product-information')?.id,
    };

    const closestSectionId = /** @type {keyof typeof SECTION_ID_MAP | undefined} */ (
      Object.keys(SECTION_ID_MAP).find((sectionId) => this.closest(sectionId))
    );

    if (closestSectionId) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=${SECTION_ID_MAP[closestSectionId]}&${params.join('&')}`;
    }

    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * Uses returned single-variant JSON id to set form id + URL param (Safari-safe).
   * Unlocks the form only when the latest request resolves.
   * @param {string} requestUrl
   * @param {string} [morphElementSelector]
   * @param {number} seq
   */
  fetchUpdatedSection(requestUrl, morphElementSelector, seq) {
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;

        const html = new DOMParser().parseFromString(responseText, 'text/html');
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) return;

        let returnedVariant = null;
        try {
          returnedVariant = JSON.parse(textContent);
        } catch {
          returnedVariant = null;
        }

        const returnedVariantId = returnedVariant?.id ? String(returnedVariant.id) : null;

        // ✅ THE KEY FIX: set the true id (works on Safari)
        if (returnedVariantId) {
          this.updateProductFormVariantId(returnedVariantId);
          this.updateUrlVariantParam(returnedVariantId);
        }

        // Unlock only if this is the latest request
        if (seq === this.#updateSeq) {
          this.#isUpdatingVariant = false;
          this.#setFormDisabled(false);
        }

        if (morphElementSelector === 'main') {
          this.updateMain(html);
        } else if (morphElementSelector) {
          this.updateElement(html, morphElementSelector);
        } else {
          const newProduct = this.updateVariantPicker(html);

          if (this.selectedOptionId) {
            this.dispatchEvent(
              new VariantUpdateEvent(returnedVariant, this.selectedOptionId, {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
              })
            );
          }
        }
      })
      .catch((error) => {
        // Ensure we don't leave form disabled on errors
        if (seq === this.#updateSeq) {
          this.#isUpdatingVariant = false;
          this.#setFormDisabled(false);
        }

        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * Re-renders the variant picker.
   * @param {Document | Element} newHtml
   * @returns {{id: string, url: string} | undefined}
   */
  updateVariantPicker(newHtml) {
    /** @type {{id: string, url: string} | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());
    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource, {
      ...MORPH_OPTIONS,
      getNodeKey: (node) => {
        if (!(node instanceof HTMLElement)) return undefined;
        return node.dataset.key;
      },
    });

    this.updateVariantPickerCss();
    return newProduct;
  }

  updateVariantPickerCss() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    const measurements = fieldsets.map((_, index) => this.#getFieldsetMeasurements(index)).filter((m) => m !== null);
    for (const measurement of measurements) this.#applyFieldsetMeasurements(measurement);
  }

  #getFieldsetMeasurements(fieldsetIndex) {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    const fieldset = fieldsets[fieldsetIndex];
    const checkedIndices = this.#checkedIndices[fieldsetIndex];
    const radios = this.#radios[fieldsetIndex];

    if (!radios || !checkedIndices || !fieldset) return null;

    const [currentIndex, previousIndex] = checkedIndices;

    return {
      fieldset,
      currentIndex,
      previousIndex,
      currentWidth: currentIndex !== undefined ? radios[currentIndex]?.parentElement?.offsetWidth : undefined,
      previousWidth: previousIndex !== undefined ? radios[previousIndex]?.parentElement?.offsetWidth : undefined,
    };
  }

  #applyFieldsetMeasurements({ fieldset, currentWidth, previousWidth, currentIndex, previousIndex }) {
    if (currentWidth) fieldset.style.setProperty('--pill-width-current', `${currentWidth}px`);
    else if (currentIndex !== undefined) fieldset.style.removeProperty('--pill-width-current');

    if (previousWidth) fieldset.style.setProperty('--pill-width-previous', `${previousWidth}px`);
    else if (previousIndex !== undefined) fieldset.style.removeProperty('--pill-width-previous');
  }

  updateFieldsetCss(fieldsetIndex) {
    if (Number.isNaN(fieldsetIndex)) return;
    const measurements = this.#getFieldsetMeasurements(fieldsetIndex);
    if (measurements) this.#applyFieldsetMeasurements(measurements);
  }

  /**
   * Updates the selected option (visual state).
   * @param {string | Element} target
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);
      if (!targetElement) throw new Error('Target element not found');
      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);
      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }
      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  updateElement(newHtml, elementSelector) {
    const element = this.closest(elementSelector);
    const newElement = newHtml.querySelector(elementSelector);

    if (!element || !newElement) {
      throw new Error(`No new element source found for ${elementSelector}`);
    }

    morph(element, newElement);
  }

  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  get selectedOption() {
    const el = this.querySelector('select option[selected], fieldset input:checked');
    if (!(el instanceof HTMLInputElement || el instanceof HTMLOptionElement)) return undefined;
    return el;
  }

  // Tolerant: never throws
  get selectedOptionId() {
    return this.selectedOption?.dataset?.optionValueId;
  }

  // Tolerant: never throws
  get selectedOptionsValues() {
    const nodes = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));
    return nodes
      .map((node) => node?.dataset?.optionValueId)
      .filter((v) => typeof v === 'string' && v.length > 0);
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}